import { createPinia, setActivePinia, defineStore as defineStore$1 } from "pinia";
import { createPinia as createPinia2, setActivePinia as setActivePinia2 } from "pinia";
let globalStore = null;
const piniaReadyCallbacks = [];
let extensionMessageListener = null;
const EXTENSION_MESSAGE_TYPES = {
  STATE_UPDATE: "gitcoffee-store:state-update",
  INITIAL_STATE: "gitcoffee-store:initial-state"
};
const generateId = () => {
  return Math.random().toString(36).substr(2, 9);
};
const contextId = generateId();
const isNuxt = () => {
  return typeof window !== "undefined" && window.$nuxt !== void 0;
};
const isNuxt3 = () => {
  var _a;
  return typeof window !== "undefined" && isNuxt() && ((_a = window.$nuxt) == null ? void 0 : _a.$pinia) !== void 0;
};
const getNuxtPinia = () => {
  var _a;
  if (typeof window !== "undefined" && ((_a = window.$nuxt) == null ? void 0 : _a.$pinia)) {
    return window.$nuxt.$pinia;
  }
  return null;
};
const isBrowserExtension = () => {
  if (typeof chrome !== "undefined" && chrome.runtime && chrome.runtime.id) {
    return true;
  }
  if (typeof browser !== "undefined" && browser.runtime && browser.runtime.id) {
    return true;
  }
  if (typeof window !== "undefined" && window.chrome && window.chrome.runtime && window.chrome.runtime.id) {
    return true;
  }
  return false;
};
const getExtensionRuntime = () => {
  if (typeof chrome !== "undefined" && chrome.runtime) {
    return chrome.runtime;
  }
  if (typeof browser !== "undefined" && browser.runtime) {
    return browser.runtime;
  }
  if (typeof window !== "undefined" && window.chrome && window.chrome.runtime) {
    return window.chrome.runtime;
  }
  return null;
};
const sendExtensionMessage = (message) => {
  const runtime = getExtensionRuntime();
  if (runtime) {
    try {
      if (runtime.sendMessage) {
        runtime.sendMessage(message);
      } else if (runtime.connect) {
        const port = runtime.connect({ name: "gitcoffee-store" });
        port.postMessage(message);
        port.disconnect();
      }
    } catch (error) {
      console.warn("GitCoffee Store: Failed to send extension message:", error);
    }
  }
};
const createExtensionSyncPlugin = () => {
  return (context) => {
    const { store } = context;
    store.$subscribe((mutation, state) => {
      if (isBrowserExtension()) {
        sendExtensionMessage({
          type: EXTENSION_MESSAGE_TYPES.STATE_UPDATE,
          payload: {
            storeId: store.$id,
            mutation,
            state,
            contextId
          }
        });
      }
    });
  };
};
const initExtensionMessageListener = () => {
  if (!isBrowserExtension() || extensionMessageListener) return;
  const runtime = getExtensionRuntime();
  if (!runtime) return;
  extensionMessageListener = (message, _sender, sendResponse) => {
    var _a;
    if (((_a = message.payload) == null ? void 0 : _a.contextId) === contextId) return;
    switch (message.type) {
      case EXTENSION_MESSAGE_TYPES.STATE_UPDATE:
        const { storeId, state } = message.payload;
        if (storeId && state) {
          console.warn("GitCoffee Store: State update received for store", storeId);
        }
        break;
      case EXTENSION_MESSAGE_TYPES.INITIAL_STATE:
        if (sendResponse) {
          console.warn("GitCoffee Store: Initial state request received");
          sendResponse({
            stores: {},
            contextId
          });
        }
        break;
    }
  };
  if (runtime.onMessage) {
    runtime.onMessage.addListener(extensionMessageListener);
  } else if (runtime.onConnect) {
    runtime.onConnect.addListener((port) => {
      if (port.name === "gitcoffee-store") {
        port.onMessage.addListener(extensionMessageListener);
      }
    });
  }
};
const cleanupExtensionMessageListener = () => {
  if (!isBrowserExtension() || !extensionMessageListener) return;
  const runtime = getExtensionRuntime();
  if (!runtime) return;
  if (runtime.onMessage) {
    runtime.onMessage.removeListener(extensionMessageListener);
  }
  extensionMessageListener = null;
};
function defineStore(id, options) {
  try {
    const useStore = defineStore$1(id, options);
    return function wrappedUseStore() {
      try {
        if (!globalStore) {
          const defaultPinia = createPinia();
          setActivePinia(defaultPinia);
          globalStore = defaultPinia;
        }
        return useStore();
      } catch (error) {
        console.error("GitCoffee Store: Error in useStore:", error);
        const mockStore = {
          $id: id,
          $state: options.state ? options.state() : {},
          $getters: {},
          $actions: {},
          $subscribe: () => {
          },
          $onAction: () => {
          },
          $reset: function() {
            this.$state = options.state ? options.state() : {};
            Object.assign(this, this.$state);
          },
          ...options.state ? options.state() : {}
        };
        if (options.getters) {
          Object.keys(options.getters).forEach((key) => {
            Object.defineProperty(mockStore, key, {
              get: () => {
                try {
                  return options.getters[key](mockStore);
                } catch (e) {
                  console.error(`GitCoffee Store: Error calling getter ${key}:`, e);
                  return void 0;
                }
              },
              enumerable: true
            });
          });
        }
        if (options.actions) {
          Object.keys(options.actions).forEach((key) => {
            mockStore[key] = (...args) => {
              try {
                return options.actions[key].apply(mockStore, args);
              } catch (e) {
                console.error(`GitCoffee Store: Error calling action ${key}:`, e);
                return void 0;
              }
            };
          });
        }
        console.warn("GitCoffee Store: Using fallback mock store for", id, "due to unexpected error.");
        return mockStore;
      }
    };
  } catch (error) {
    console.error("GitCoffee Store: Critical error in defineStore:", error);
    return function useStore() {
      return {
        $id: id,
        $state: {},
        $getters: {},
        $actions: {},
        $subscribe: () => {
        },
        $onAction: () => {
        },
        $reset: () => {
        }
      };
    };
  }
}
const initStore = async (app, options) => {
  try {
    if (isNuxt3()) {
      console.log("GitCoffee Store: Using Nuxt 3 Pinia");
      const nuxtPinia = getNuxtPinia();
      if (nuxtPinia && options) {
        if (options.plugins) {
          options.plugins.forEach((plugin) => nuxtPinia.use(plugin));
        }
      }
      if (nuxtPinia) {
        setActivePinia(nuxtPinia);
        globalStore = nuxtPinia;
        piniaReadyCallbacks.forEach((callback) => callback());
        piniaReadyCallbacks.length = 0;
        console.log("GitCoffee Store: Nuxt 3 Pinia initialized successfully");
        return nuxtPinia;
      }
      console.warn("GitCoffee Store: Failed to get Nuxt 3 Pinia instance");
      return null;
    }
    console.log("GitCoffee Store: Using standard Pinia");
    const createdPinia = createPinia();
    if (options) {
      if (options.plugins) {
        options.plugins.forEach((plugin) => createdPinia.use(plugin));
      }
    }
    if (isBrowserExtension()) {
      console.log("GitCoffee Store: Adding extension sync plugin");
      createdPinia.use(createExtensionSyncPlugin());
    }
    if (app) {
      app.use(createdPinia);
    }
    setActivePinia(createdPinia);
    globalStore = createdPinia;
    if (isBrowserExtension()) {
      initExtensionMessageListener();
    }
    piniaReadyCallbacks.forEach((callback) => callback());
    piniaReadyCallbacks.length = 0;
    console.log("GitCoffee Store: Standard Pinia initialized successfully");
    return createdPinia;
  } catch (error) {
    console.error("GitCoffee Store: Error initializing Pinia:", error);
    const fallbackPinia = createPinia();
    setActivePinia(fallbackPinia);
    globalStore = fallbackPinia;
    console.warn("GitCoffee Store: Using fallback Pinia instance");
    return fallbackPinia;
  }
};
if (!globalStore) {
  try {
    const defaultPinia = createPinia();
    setActivePinia(defaultPinia);
    globalStore = defaultPinia;
    console.log("GitCoffee Store: Created default Pinia instance on module load");
  } catch (error) {
    console.warn("GitCoffee Store: Failed to create default Pinia instance on module load:", error);
  }
}
const waitForPinia = () => {
  return new Promise((resolve) => {
    var _a;
    if (globalStore || typeof window !== "undefined" && ((_a = window.$nuxt) == null ? void 0 : _a.$pinia)) {
      resolve();
    } else {
      piniaReadyCallbacks.push(() => resolve());
    }
  });
};
const defineNuxtStore = defineStore;
const cleanupStore = () => {
  if (isBrowserExtension()) {
    cleanupExtensionMessageListener();
  }
  globalStore = null;
};
const version = "1.0.0";
export {
  cleanupStore,
  createPinia2 as createPinia,
  defineNuxtStore,
  defineStore,
  getExtensionRuntime,
  getNuxtPinia,
  initStore,
  isBrowserExtension,
  isNuxt,
  isNuxt3,
  setActivePinia2 as setActivePinia,
  globalStore as store,
  version,
  waitForPinia
};
//# sourceMappingURL=index.mjs.map
